/**
 * 트렌드 카드 표시 공통 헬퍼.
 * 사용자 결정 2026-05-03:
 * - 변화량: 일평균 차이 → 주간 환산 + "주간 SNS +N건" 명시 표현
 * - 검색 방향: rising / flat / falling / NULL 4가지 모두 명시 라벨
 *
 * 사용자 결정 2026-05-07 (룰 v4):
 * - 점수 표시 X — stage + 시그널 텍스트로 본질 전달
 * - 점수 자리 → stage 안 순위 ("#1 / 196") 또는 영향력 시그널
 */

export interface StageRank {
  text: string;        // "#1 / 196개"
  short: string;       // "#1"
}

/**
 * stage 안 순위 표시. 점수 큰 숫자 자리에 들어감.
 */
export function formatStageRank(rank: number, total?: number | null): StageRank {
  return {
    text: total ? `#${rank} / ${total}` : `#${rank}`,
    short: `#${rank}`,
  };
}

/**
 * 30일 멘션 영향력 표시. mature/growing 카드의 핵심 시그널.
 */
export function formatMentionImpact(m30: number | null | undefined): string | null {
  if (m30 == null || m30 === 0) return null;
  return `30일 ${m30.toLocaleString()}건`;
}

export interface WeeklyChange {
  text: string;        // "주간 SNS +14건" 또는 "주간 SNS -22건"
  short: string;       // "+14" 또는 "-22" — 좁은 카드용
  color: string;       // 색상 hex
  isPositive: boolean;
}

/**
 * mention_change_per_day (일평균 차이) → 주간 환산 + 명시 표현.
 * 0/null 이면 null 반환 (베타 안전망 — 의미 있을 때만 표시).
 */
export function formatWeeklyChange(
  perDay: number | null | undefined,
  context: "surge" | "decline" = "surge",
): WeeklyChange | null {
  if (perDay == null || perDay === 0) return null;
  const weekly = Math.round(perDay * 7);
  if (weekly === 0) return null;

  const isPositive = weekly > 0;
  // 색상 — surge 컨텍스트 (양수 호재) vs decline 컨텍스트 (음수 호재)
  const color =
    context === "decline"
      ? weekly <= -20 ? "#60A5FA"      // 강한 하락 (파란)
        : weekly < 0 ? "#93C5FD"        // 약한 하락 (연한 파란)
        : "#10B981"                     // 양수 = 회복 (녹)
      : weekly >= 20 ? "#FF5C35"        // 강한 상승 (주황)
        : weekly > 0 ? "#10B981"        // 약한 상승 (녹)
        : "#60A5FA";                    // 음수 (파)

  return {
    text: isPositive ? `주간 SNS +${weekly}건` : `주간 SNS ${weekly}건`,
    short: isPositive ? `+${weekly}` : `${weekly}`,
    color,
    isPositive,
  };
}

export interface SearchDirection {
  label: string;       // "🔺 검색 상승" / "→ 검색 보합" / "🔻 검색 하락" / "검색 데이터 부족"
  shortLabel: string;  // = label (사용자 결정 2026-05-07 — "검색" 명시로 직관성 ↑)
  color: string;
  available: boolean;  // false = NULL (데이터 자체 없음)
}

/**
 * datalab_direction → 명시 라벨. 4가지 모두 표시 (사용자 결정 2026-05-03 옵션 C).
 */
export function formatSearchDirection(
  direction: string | null | undefined,
): SearchDirection {
  // 사용자 결정 2026-05-07 — shortLabel = label 동일 ("검색" 명시로 직관성 ↑)
  // 이전: shortLabel = "🔺 상승" (무엇이 상승? 모호)
  // 변경: shortLabel = "🔺 검색 상승" (즉시 이해)
  if (direction === "rising") {
    return { label: "🔺 검색 상승", shortLabel: "🔺 검색 상승", color: "#FF5C35", available: true };
  }
  if (direction === "falling") {
    return { label: "🔻 검색 하락", shortLabel: "🔻 검색 하락", color: "#60A5FA", available: true };
  }
  if (direction === "flat") {
    return { label: "→ 검색 보합", shortLabel: "→ 검색 보합", color: "#B0B0B0", available: true };
  }
  return {
    label: "검색 데이터 부족",
    shortLabel: "검색 데이터 부족",
    color: "#6B7280",   // 더 어두운 회색
    available: false,
  };
}
